import React, { useState, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { EmojiEmotions, Block, AdminPanelSettings, Person, PersonAdd, PersonRemove, AddModerator, RemoveModerator, FaceRetouchingOff, AddReaction, ArrowForwardIos, ArrowBackIos, KeyboardDoubleArrowRight, KeyboardDoubleArrowLeft, LibraryBooks } from '@mui/icons-material';
import { Button, Table, TableBody, TableCell, TableHead, TableRow, IconButton, Tooltip, CircularProgress, Modal, Box, Tabs } from '@mui/material';

import { useTable, useSortBy, usePagination, useGlobalFilter, useAsyncDebounce } from 'react-table'
import FormatError from './FormatError'
import RemoteButton from './RemoteButton'

const emailRegex = /^[\w.+]+@\w+[.]\w+$/;

const theme = createTheme({
    components: {
        // Name of the component
        MuiTableCell: {
            defaultProps: {
                size: 'small'
            },
        },
    },
});

function SetTagButton(props) {
    const [value, setValue] = useState(props.value);
    return (
        <RemoteButton name={value ? value : 'Set'} onStart={() => props.onStart(value)}>
            Tag: <input value={value} onChange={e => setValue(e.target.value)} />
        </RemoteButton>
    )
}

function SetExpirationDateButton(props) {
    const [value, setValue] = useState(props.value);
    return (
        <RemoteButton name={value ? value : 'Set Expiration Date'} onStart={() => props.onStart(value)}>
            <p>1. Date format: YYYY/MM/DD HH:MM:SS</p>
            <p>2. Set empty value to disable expiration.</p>
            Expiration Date: <input value={value} onChange={e => setValue(e.target.value)} />
        </RemoteButton>
    )  
}

function GlobalFilter({
    preGlobalFilteredRows,
    globalFilter,
    setGlobalFilter,
}) {
    const count = preGlobalFilteredRows.length
    const [value, setValue] = React.useState(globalFilter)
    const onChange = useAsyncDebounce(value => {
        setGlobalFilter(value || undefined)
    }, 200)

    return (
        <span>
            Search:{' '}
            <input
                value={value || ""}
                onChange={e => {
                    setValue(e.target.value);
                    onChange(e.target.value);
                }}
                placeholder={`${count} records...`}
                style={{
                    fontSize: '1.1rem',
                    border: '0',
                }}
            />
        </span>
    )
}

function MUTable({ columns, data, loading }) {
    const {
        getTableProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        visibleColumns,
        preGlobalFilteredRows,
        setGlobalFilter,
        state,
    } = useTable(
        {
            columns,
            data,
            initialState: {
                sortBy: [
                    {
                        id: 'disable',
                        desc: false
                    },
                    {
                        id: 'lastUpdated',
                        desc: true
                    }
                ]
            }
        },
        useGlobalFilter,
        useSortBy,
        usePagination
    )
    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        bgcolor: 'background.paper',
        border: '0px solid #000',
        outline: 0,
        p: 4,
    };
    // Render the UI for your table
    return (
        <>
            <ThemeProvider theme={theme}>

                <Modal
                    open={loading}
                    aria-labelledby="modal-modal-title"
                    aria-describedby="modal-modal-description"
                >
                    <Box sx={style}>
                        <CircularProgress />
                    </Box>
                </Modal>
                <Table {...getTableProps()}>
                    <TableHead>
                        {headerGroups.map(headerGroup => (
                            <TableRow {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map(column => (
                                    <TableCell {...column.getHeaderProps(column.getSortByToggleProps())}>
                                        {column.render('Header')}
                                        { }
                                        <span>
                                            {column.isSorted
                                                ? column.isSortedDesc
                                                    ? ' 🔽'
                                                    : ' 🔼'
                                                : ''}
                                        </span>
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                        <TableRow>
                            <TableCell
                                colSpan={visibleColumns.length}
                                style={{
                                    textAlign: 'left',
                                }}
                            >
                                <GlobalFilter
                                    preGlobalFilteredRows={preGlobalFilteredRows}
                                    globalFilter={state.globalFilter}
                                    setGlobalFilter={setGlobalFilter}
                                />
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {page.map((row, i) => {
                            prepareRow(row)
                            return (
                                <TableRow {...row.getRowProps()}>
                                    {row.cells.map(cell => {
                                        return (
                                            <TableCell {...cell.getCellProps()}>
                                                {cell.render('Cell')}
                                            </TableCell>
                                        )
                                    })}
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
                <div className="pagination">
                    <Button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                        <KeyboardDoubleArrowLeft />
                    </Button>
                    <Button onClick={() => previousPage()} disabled={!canPreviousPage}>
                        <ArrowBackIos />
                    </Button>
                    <Button onClick={() => nextPage()} disabled={!canNextPage}>
                        <ArrowForwardIos />
                    </Button>
                    <Button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
                        <KeyboardDoubleArrowRight />
                    </Button>
                </div>
            </ThemeProvider>
        </>
    )
}

export default function UserTable(props) {
    const [gmail, setGmail] = useState('');
    const [error, setError] = useState(null);
    const [adding, setAdding] = useState(false);
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [tag, setTag] = useState('');

    useEffect(() => {
        listUsers();
    }, []);

    const handleTag = id => e => {
        setTag(e.target.value);
    }

    const setUserTag = id => async (tagValue) => {
        const response = await fetch('/api/admin/user/set_tag', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                id,
                tag: tagValue,
            }),
        });
        const result = await response.json();
        if (result.error) {
            setError(result.error);
        } else {
            listUsers();
        }
    }

    const setUserExpirationDate = id => async (date) => {
        const response = await fetch('/api/admin/user/set_expiration_date', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                id,
                date: date ? new Date(date) : null,
            }),
        });
        const result = await response.json();
        if (result.error) {
            setError(result.error);
        } else {
            listUsers();
        }
    }

    const handleGmailChange = e => {
        setGmail(e.target.value);
    }

    const listUsers = async () => {
        setLoading(true);
        const response = await fetch('/api/admin/user/list');
        const result = await response.json();
        setUsers(result.result.users);
        setLoading(false);
        console.log(result.result.users);
    }

    const addOneUser = async (admin) => {
        setAdding(true);
        fetch('/api/admin/user/add', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                name: "",
                gmail: gmail,
                admin,
            }),
        }).then(response => response.json())
            .then(result => {
                if (result.result === 'OK') {
                    setAdding(false);
                } else {
                    setAdding(false);
                    setError(result.error);
                }
                listUsers();
            })
    }

    const addUser = () => {
        addOneUser(false);
    }

    const addAdmin = () => {
        addOneUser(true);
    }

    const setAdmin = (id, admin) => {
        return async () => {
            const response = await fetch('/api/admin/user/set_admin', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id,
                    admin: !!admin,
                }),
            });
            const result = await response.json();
            if (result.result === 'OK') {
                listUsers();
            } else {
                setError(result.error);
            }
        }
    }

    const setPlayerOnlyAccess = (id, playerOnlyAccess) => {
        return async () => {
            const response = await fetch('/api/admin/user/set_player_only_access', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id,
                    playerOnlyAccess: !!playerOnlyAccess,
                }),
            });
            const result = await response.json();
            if (result.result === 'OK') {
                listUsers();
            } else {
                setError(result.error);
            }
        }
    }

    const disableUser = (id) => {
        return async () => {
            const response = await fetch('/api/admin/user/disable', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id,
                }),
            });
            const result = await response.json();
            if (result.result === 'OK') {
                listUsers();
            } else {
                setError(result.error);
            }
        }
    }

    const enableUser = (id) => {
        return async () => {
            const response = await fetch('/api/admin/user/enable', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id,
                }),
            });
            const result = await response.json();
            if (result.result === 'OK') {
                listUsers();
            } else {
                setError(result.error);
            }
        }
    }

    const columns = React.useMemo(
        () => [
            {
                Header: 'Name',
                accessor: 'name',
            },
            {
                Header: 'Display Name',
                accessor: 'displayName',
            },
            {
                Header: 'Email',
                accessor: 'email',
            },
            {
                Header: 'Tag',
                id: 'tag',
                Cell: ({ row: { original } }) => (
                    <div>
                        <SetTagButton value={original.user_tags[0] ? original.user_tags[0].tag : ''} onStart={setUserTag(original.id)} />
                    </div>

                )
            },
            {
                Header: 'Invited By',
                accessor: 'invitorEmail',
            },
            {
                Header: 'Last Updated',
                id: 'lastUpdated',
                accessor: (row, rowIndex) => new Date(row.updatedAt),
                sortType: 'datetime',
                Cell: ({ cell: { value }}) => value.toLocaleDateString()
            },
            {
                Header: 'Roles',
                accessor: (row, rowIndex) => row.disable ? 'true' : 'false',
                id: 'disable',
                Cell: ({ row: { original } }) => (
                    <div>
                        {
                            original.permissions.includes("ARCADE/SUPER_PERMISSION[DEPRECATED]") &&
                            <Tooltip title="Admin">
                                {/* <IconButton color="primary" aria-label="admin" component="span"> */}
                                <AdminPanelSettings />
                                {/* </IconButton> */}
                            </Tooltip>
                        }
                        {
                            !original.disable && !original.permissions.includes("ARCADE/MAP/PUBLISH") &&
                            <Tooltip title="Enabled User">
                                {/* <IconButton color="primary" aria-label="admin" component="span"> */}
                                <Person />
                                {/* </IconButton> */}
                            </Tooltip>
                        }
                        {
                            !original.disable && original.permissions.includes("ARCADE/MAP/PUBLISH") &&
                            <Tooltip title="Designer">
                                <EmojiEmotions />
                            </Tooltip>
                        }
                        {
                            original.disable &&
                            <Tooltip title="Disabled User">
                                <Block />
                            </Tooltip>
                        }
                    </div>
                ),
            },
            {
                Header: 'Operations',
                accessor: 'operations',
                Cell: ({ row: { original } }) => {
                    return (
                        <div>
                            {
                                !original.disable && !original.permissions.includes("ARCADE/SUPER_PERMISSION[DEPRECATED]") &&
                                <Tooltip title="Make Admin">
                                    <IconButton color="secondary" aria-label="disabled" component="span" onClick={setAdmin(original.id, true)}>
                                        <AddModerator />
                                    </IconButton>
                                </Tooltip>
                            }
                            {
                                !original.disable && original.permissions.includes("ARCADE/SUPER_PERMISSION[DEPRECATED]") &&
                                <Tooltip title="Remove Admin">
                                    <IconButton color="secondary" aria-label="admin" component="span" alt="" onClick={setAdmin(original.id, false)}>
                                        <RemoveModerator />
                                    </IconButton>
                                </Tooltip>
                            }
                            {
                                original.disable &&
                                <Tooltip title="Enable User">
                                    <IconButton color="secondary" aria-label="disabled" component="span" onClick={enableUser(original.id)}>
                                        <PersonAdd />
                                    </IconButton>
                                </Tooltip>
                            }
                            {
                                !original.disable &&
                                !original.permissions.includes("ARCADE/SUPER_PERMISSION[DEPRECATED]") &&
                                <Tooltip title="Disable User">
                                    <IconButton color="secondary" aria-label="disabled" component="span" onClick={disableUser(original.id)}>
                                        <PersonRemove />
                                    </IconButton>
                                </Tooltip>
                            }
                            {
                                !original.disable && !original.permissions.includes("ARCADE/MAP/PUBLISH") &&
                                !original.permissions.includes("ARCADE/SUPER_PERMISSION[DEPRECATED]") &&
                                <Tooltip title="Add Publish Rights">
                                    <IconButton color="secondary" aria-label="player only access" component="span" onClick={setPlayerOnlyAccess(original.id, false)}>
                                        <AddReaction />
                                    </IconButton>
                                </Tooltip>
                            }
                            {
                                !original.disable && original.permissions.includes("ARCADE/MAP/PUBLISH") &&
                                !original.permissions.includes("ARCADE/SUPER_PERMISSION[DEPRECATED]") &&
                                <Tooltip title="Remove Publish Rights">
                                    <IconButton color="secondary" aria-label="player only access" component="span" onClick={setPlayerOnlyAccess(original.id, true)}>
                                        <FaceRetouchingOff />
                                    </IconButton>
                                </Tooltip>
                            }
                            <Tooltip title="Activity Log">
                                <a target="_blank" href={`/admin/logs?id=${original.id}`}>
                                    <IconButton color="primary" aria-label="activity log" component="span">
                                        <LibraryBooks />
                                    </IconButton>
                                </a>
                            </Tooltip>
                            {
                                <div>
                                    <SetExpirationDateButton value={original.expiresAt ? new Date(original.expiresAt).toLocaleString() : ''} onStart={setUserExpirationDate(original.id)} />
                                </div>
                            }
                        </div>
                    )
                },
            }
        ],
        []
    )


    return (
        <div>
            <div>
                <div>
                    {error && FormatError(error)}
                </div>
                <h1>Add User</h1>
                <input value={gmail} onChange={handleGmailChange} />
                {
                    adding && <CircularProgress />
                }
                {
                    !adding &&
                    <Button onClick={addUser}>Add</Button>
                }
                {
                    !adding &&
                    <Button onClick={addAdmin}>Add Admin</Button>
                }
                {gmail && !emailRegex.test(gmail) && FormatError(`${gmail} is not valid email address.`)}
            </div>
            <MUTable columns={columns} data={users} loading={loading} />
        </div>
    )
}
